import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  Mail,
  MessageSquare,
  Phone,
  Plus,
  Search,
  Star,
  Users,
  UserPlus,
  X,
} from "lucide-react";
import { cn } from "@/lib/cn";
import { ROUTES } from "@/constants/routes";
import { queryKeys } from "@/constants/queryKeys";
import {
  createDirectoryContact,
  createDirectoryContactGroup,
  fetchChatDirectory,
  fetchDirectoryContactGroups,
  fetchDirectoryContacts,
} from "@/services/api";
import { ApiError } from "@/services/http/errors";
import { useAuthStore, useFavouritesStore, useUiStore, favouriteKeyFromPhone } from "@/store";
import { usePhoneStore } from "@/features/phone";
import { useMessagesStore } from "@/features/messages/store/useMessagesStore";
import {
  openOrCreateDmForContact,
  openOrCreateSelfDm,
} from "@/features/messages/lib/openOrCreateDm";
import { FloatingDialerButton } from "@/components/dialer/FloatingDialerButton";
import { Button, Field, Input, Modal, Select, Textarea, toast } from "@/components/ui";
import {
  mapDirectoryContact,
  mapDirectoryContactGroup,
  mapInternalContact,
  mapSelfContact,
  type ContactGroup,
  type ContactRecord,
} from "../data/types";
type SidebarTab = "all" | "internal" | "external";
type Selection =
  | { type: "contact"; id: string }
  | { type: "group"; id: string }
  | null;
type ModalKind = "contact" | "group" | null;

const emptyContactForm = {
  name: "",
  phone: "",
  email: "",
  description: "",
  groupId: "",
};

const emptyGroupForm = {
  name: "",
  description: "",
};

function formatDate(value?: string) {
  if (!value) return "";
  try {
    return new Date(value).toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return value;
  }
}

export function ContactsWorkspace() {
  const navigate = useNavigate();
  const openDialer = useUiStore((s) => s.openDialer);
  const makeCall = usePhoneStore((s) => s.makeCall);
  const setDialNumber = usePhoneStore((s) => s.setDialNumber);
  const regStatus = usePhoneStore((s) => s.regStatus);
  const profile = useAuthStore((s) => s.profile);
  const setActiveConversationId = useMessagesStore((s) => s.setActiveConversationId);
  const queryClient = useQueryClient();
  const [chatOpeningId, setChatOpeningId] = useState<string | null>(null);

  const dialNumber = (phone?: string) => {
    const dest = phone?.trim();
    if (!dest) {
      toast.error("No phone number", "This contact has no number to dial.");
      return;
    }
    setDialNumber(dest);
    openDialer();
    makeCall(dest);
    if (regStatus !== "online") {
      toast.warning("Phone offline", "Number set on dialer. Wait for registration, then dial.");
    }
  };

  const openChatWithContact = async (contact: ContactRecord) => {
    if (chatOpeningId) return;
    setChatOpeningId(contact.id);
    try {
      let conversationId: string | null = null;

      if (contact.isMe) {
        if (profile?.id == null) {
          toast.error("Can't start chat", "Your profile is not loaded yet. Try again.");
          return;
        }
        // Dedicated path — never match by phone/name (that opens someone else's DM).
        conversationId = await openOrCreateSelfDm(Number(profile.id));
      } else {
        const peerUserId =
          contact.source === "internal" && contact.id.startsWith("int-")
            ? Number(contact.id.slice(4))
            : undefined;

        conversationId = await openOrCreateDmForContact({
          phone: contact.phone,
          email: contact.email,
          name: contact.name,
          peerUserId: Number.isFinite(peerUserId) ? peerUserId : undefined,
        });
      }

      if (!conversationId) {
        toast.error(
          "Can't start chat",
          contact.isMe
            ? "Could not open your notes chat. Try again."
            : "No matching teammate found for this contact. Chat works with Papaya users in your org.",
        );
        return;
      }

      setActiveConversationId(conversationId);
      await queryClient.invalidateQueries({ queryKey: ["chat-conversations"] });
      navigate(ROUTES.messages, { state: { conversationId } });
    } catch {
      toast.error(
        "Could not open chat",
        contact.isMe
          ? "Could not create your notes chat. Restart the server if this keeps failing."
          : "Something went wrong. Try again.",
      );
    } finally {
      setChatOpeningId(null);
    }
  };

  const isAdmin = Boolean(profile?.is_supervisor || profile?.is_superuser);
  const [tab, setTab] = useState<SidebarTab>("all");
  const [query, setQuery] = useState("");
  const [selection, setSelection] = useState<Selection>(null);
  const [modal, setModal] = useState<ModalKind>(null);
  const [contactForm, setContactForm] = useState(emptyContactForm);
  const [groupForm, setGroupForm] = useState(emptyGroupForm);
  const [formError, setFormError] = useState<string | null>(null);

  const groupsQuery = useQuery({
    queryKey: queryKeys.directoryContactGroups,
    queryFn: fetchDirectoryContactGroups,
  });

  const contactsQuery = useQuery({
    queryKey: queryKeys.directoryContacts("all", ""),
    queryFn: () => fetchDirectoryContacts(),
  });

  const teamQuery = useQuery({
    queryKey: queryKeys.chatDirectory(""),
    queryFn: () => fetchChatDirectory(),
  });

  const favouriteItems = useFavouritesStore((s) => s.items);

  const invalidateContacts = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: queryKeys.directoryContactGroups }),
      queryClient.invalidateQueries({ queryKey: ["directory-contacts"] }),
      queryClient.invalidateQueries({ queryKey: ["chat-directory"] }),
    ]);
  };

  const createContactMutation = useMutation({
    mutationFn: createDirectoryContact,
    onSuccess: async (created) => {
      await invalidateContacts();
      setModal(null);
      setFormError(null);
      setContactForm(emptyContactForm);
      setTab("external");
      setSelection({ type: "contact", id: `ext-${created.id}` });
      toast.success("Contact created", created.name);
    },
    onError: (err) => {
      const message =
        err instanceof ApiError ? err.userMessage : "Could not create contact.";
      setFormError(message);
      toast.error("Could not create contact", message);
    },
  });

  const createGroupMutation = useMutation({
    mutationFn: createDirectoryContactGroup,
    onSuccess: async (created) => {
      await invalidateContacts();
      setModal(null);
      setFormError(null);
      setGroupForm(emptyGroupForm);
      setTab("external");
      toast.success("Group created", created.name);
    },
    onError: (err) => {
      const message =
        err instanceof ApiError ? err.userMessage : "Could not create group.";
      setFormError(message);
      toast.error("Could not create group", message);
    },
  });

  const favouriteKeys = useMemo(
    () => new Set(favouriteItems.map((item) => item.key)),
    [favouriteItems],
  );

  const externalContacts = useMemo((): ContactRecord[] => {
    return (contactsQuery.data ?? []).map((dto) => {
      const contact = mapDirectoryContact(dto);
      const key = favouriteKeyFromPhone(contact.phone);
      return { ...contact, favourite: key ? favouriteKeys.has(key) : false };
    });
  }, [contactsQuery.data, favouriteKeys]);

  const internalContacts = useMemo((): ContactRecord[] => {
    const markFavourite = (contact: ContactRecord): ContactRecord => {
      const key = favouriteKeyFromPhone(contact.phone);
      return { ...contact, favourite: key ? favouriteKeys.has(key) : false };
    };

    const me = profile ? markFavourite(mapSelfContact(profile)) : null;
    const others = (teamQuery.data ?? [])
      .filter((user) => !profile || user.id !== profile.id)
      .map((user) => markFavourite(mapInternalContact(user)))
      .sort((a, b) => a.name.localeCompare(b.name));

    return me ? [me, ...others] : others;
  }, [teamQuery.data, favouriteKeys, profile]);

  const contacts = useMemo((): ContactRecord[] => {
    const rest = [...internalContacts.filter((c) => !c.isMe), ...externalContacts].sort(
      (a, b) => a.name.localeCompare(b.name),
    );
    const me = internalContacts.find((c) => c.isMe);
    return me ? [me, ...rest] : rest;
  }, [internalContacts, externalContacts]);

  const tabContacts = useMemo(() => {
    if (tab === "internal") return internalContacts;
    if (tab === "external") return externalContacts;
    return contacts;
  }, [tab, contacts, internalContacts, externalContacts]);

  const contactGroups = useMemo(() => {
    const byGroup = new Map<string, string[]>();
    for (const contact of externalContacts) {
      for (const groupId of contact.groupIds) {
        const list = byGroup.get(groupId) ?? [];
        list.push(contact.id);
        byGroup.set(groupId, list);
      }
    }
    return (groupsQuery.data ?? []).map((group) =>
      mapDirectoryContactGroup(group, byGroup.get(String(group.id)) ?? []),
    );
  }, [groupsQuery.data, externalContacts]);

  const filteredContacts = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return tabContacts;
    return tabContacts.filter(
      (item) =>
        item.name.toLowerCase().includes(q) ||
        item.phone.toLowerCase().includes(q) ||
        (item.extension?.toLowerCase().includes(q) ?? false) ||
        item.role.toLowerCase().includes(q) ||
        item.department.toLowerCase().includes(q) ||
        (item.email?.toLowerCase().includes(q) ?? false),
    );
  }, [tabContacts, query]);

  const favourites = filteredContacts.filter((item) => item.favourite);

  const alphabetGroups = useMemo(() => {
    const map = new Map<string, ContactRecord[]>();
    for (const contact of filteredContacts) {
      const letter = contact.name.charAt(0).toUpperCase() || "#";
      const list = map.get(letter) ?? [];
      list.push(contact);
      map.set(letter, list);
    }
    return [...map.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [filteredContacts]);

  useEffect(() => {
    if (selection) return;
    if (tabContacts[0]) {
      setSelection({ type: "contact", id: tabContacts[0].id });
    }
  }, [selection, tab, tabContacts]);

  const selectedContact =
    selection?.type === "contact"
      ? (contacts.find((item) => item.id === selection.id) ?? null)
      : null;

  const switchTab = (next: SidebarTab) => {
    setTab(next);
    setQuery("");
    const list =
      next === "internal"
        ? internalContacts
        : next === "external"
          ? externalContacts
          : contacts;
    setSelection(list[0] ? { type: "contact", id: list[0].id } : null);
  };

  const openAddContact = () => {
    const preferredGroupId =
      selection?.type === "group"
        ? selection.id
        : contactGroups[0]?.id ?? "";
    setContactForm({
      ...emptyContactForm,
      groupId: preferredGroupId,
    });
    setFormError(null);
    setModal("contact");
  };

  const openAddGroup = () => {
    setGroupForm(emptyGroupForm);
    setFormError(null);
    setModal("group");
  };

  const saveContact = () => {
    if (!contactForm.name.trim()) {
      setFormError("Name is required.");
      return;
    }
    setFormError(null);
    createContactMutation.mutate({
      name: contactForm.name.trim(),
      phone: contactForm.phone.trim(),
      email: contactForm.email.trim(),
      description: contactForm.description.trim(),
      group_id: contactForm.groupId ? Number(contactForm.groupId) : null,
    });
  };

  const saveGroup = () => {
    if (!groupForm.name.trim()) {
      setFormError("Group name is required.");
      return;
    }
    setFormError(null);
    createGroupMutation.mutate({
      name: groupForm.name.trim(),
      description: groupForm.description.trim(),
    });
  };

  const loading =
    groupsQuery.isLoading || contactsQuery.isLoading || teamQuery.isLoading;
  const error = groupsQuery.error || contactsQuery.error || teamQuery.error;
  const noAccess =
    !loading &&
    !error &&
    !isAdmin &&
    contacts.length === 0;
  const canAddContact = tab === "all" || tab === "external";

  const groupOptions = [
    { value: "", label: "Ungrouped" },
    ...contactGroups.map((group) => ({
      value: group.id,
      label: group.name,
    })),
  ];

  return (
    <div className="relative flex h-full min-h-0 w-full overflow-hidden rounded-[6px] border border-border bg-surface shadow-[0_12px_36px_rgba(65,33,10,0.08)] dark:shadow-[0_12px_36px_rgba(0,0,0,0.35)]">
      <aside className="flex h-full w-full flex-col lg:w-[300px] lg:max-w-[300px] lg:shrink-0 lg:border-r lg:border-border">
        <div className="flex items-center justify-between gap-2 px-4 pt-4 pb-2">
          <h2 className="text-xl font-semibold text-fg">Contacts</h2>
          <div className="flex items-center gap-1">
            {canAddContact ? (
              <IconBtn label="Add contact" onClick={openAddContact}>
                <span className="relative inline-flex">
                  <UserPlus className="h-4 w-4" />
                  <Plus className="absolute -top-1.5 -right-1.5 h-2.5 w-2.5" />
                </span>
              </IconBtn>
            ) : null}
            {isAdmin && tab === "external" ? (
              <IconBtn label="Add group" onClick={openAddGroup}>
                <span className="relative inline-flex">
                  <Users className="h-4 w-4" />
                  <Plus className="absolute -top-1.5 -right-1.5 h-2.5 w-2.5" />
                </span>
              </IconBtn>
            ) : null}
          </div>
        </div>

        <div className="space-y-2.5 px-3 pb-3">
          <div className="relative">
            <Search className="pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-fg-subtle" />
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={
                tab === "internal"
                  ? "Search team name or extension"
                  : "Search name, number or group"
              }
              className="h-10 w-full rounded-full border border-transparent bg-surface-muted pr-3 pl-9 text-sm text-fg outline-none placeholder:text-fg-subtle focus-visible:ring-2 focus-visible:ring-ring/30"
            />
          </div>

          <div className="flex flex-wrap items-center gap-1.5">
            <PillTab
              active={tab === "all"}
              label="All"
              count={contacts.length}
              onClick={() => switchTab("all")}
            />
            <PillTab
              active={tab === "internal"}
              label="Internal"
              count={internalContacts.length}
              onClick={() => switchTab("internal")}
            />
            <PillTab
              active={tab === "external"}
              label="External"
              count={externalContacts.length}
              onClick={() => switchTab("external")}
            />
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-3">
          {loading ? (
            <p className="px-3 py-8 text-center text-sm text-fg-muted">Loading contacts…</p>
          ) : error ? (
            <p className="px-3 py-8 text-center text-sm text-fg-muted">
              Could not load contacts. Check your access and try again.
            </p>
          ) : noAccess ? (
            <div className="px-3 py-8 text-center">
              <p className="text-sm font-semibold text-fg">No contacts yet</p>
              <p className="mt-2 text-xs leading-relaxed text-fg-muted">
                Add a contact to get started. Group access is optional — ask a supervisor
                if you need shared contact groups.
              </p>
              <button
                type="button"
                onClick={openAddContact}
                className="mt-3 inline-flex items-center gap-1.5 rounded-[6px] bg-papaya-deep px-3 py-1.5 text-xs font-semibold text-white hover:bg-papaya-deep/90"
              >
                <UserPlus className="h-3.5 w-3.5" />
                Add contact
              </button>
            </div>
          ) : (
            <>
              {favourites.length > 0 ? (
                <>
                  <p className="px-2 py-2 text-xs font-semibold tracking-wide text-fg-subtle uppercase">
                    Favourites
                  </p>
                  {favourites.map((item) => (
                    <ContactRow
                      key={`fav-${item.id}`}
                      contact={item}
                      active={
                        selection?.type === "contact" && selection.id === item.id
                      }
                      onSelect={() => setSelection({ type: "contact", id: item.id })}
                    />
                  ))}
                </>
              ) : null}

              {alphabetGroups.map(([letter, items]) => (
                <div key={letter}>
                  <p className="mt-3 px-2 py-2 text-xs font-semibold tracking-wide text-fg-subtle uppercase">
                    {letter}
                  </p>
                  {items.map((item) => (
                    <ContactRow
                      key={item.id}
                      contact={item}
                      active={
                        selection?.type === "contact" && selection.id === item.id
                      }
                      onSelect={() => setSelection({ type: "contact", id: item.id })}
                    />
                  ))}
                </div>
              ))}

              {filteredContacts.length === 0 ? (
                <div className="px-3 py-8 text-center">
                  <p className="text-sm text-fg-muted">
                    {tab === "internal"
                      ? "No team contacts found"
                      : "No contacts found"}
                  </p>
                  {canAddContact ? (
                    <button
                      type="button"
                      onClick={openAddContact}
                      className="mt-3 inline-flex items-center gap-1.5 rounded-[6px] bg-papaya-deep px-3 py-1.5 text-xs font-semibold text-white hover:bg-papaya-deep/90"
                    >
                      <UserPlus className="h-3.5 w-3.5" />
                      Add contact
                    </button>
                  ) : null}
                </div>
              ) : null}
            </>
          )}
        </div>
      </aside>

      <section className="relative hidden min-w-0 flex-1 flex-col bg-surface-muted lg:flex">
        {selectedContact ? (
          <ContactInfoPanel
            contact={selectedContact}
            groups={contactGroups}
            currentUserId={profile?.id}
            onClose={() => setSelection(null)}
            onDial={() => dialNumber(selectedContact.phone)}
            onMessage={() => void openChatWithContact(selectedContact)}
            messaging={chatOpeningId === selectedContact.id}
          />
        ) : (
          <div className="flex flex-1 items-center justify-center p-6 text-sm text-fg-muted">
            {loading ? "Loading…" : "Select a contact, or add a new one"}
          </div>
        )}
        <FloatingDialerButton />
      </section>

      <FloatingDialerButton className="lg:hidden" />

      <Modal
        open={modal === "contact"}
        onClose={() => {
          if (createContactMutation.isPending) return;
          setModal(null);
          setFormError(null);
        }}
        title="Add contact"
        footer={
          <>
            <Button
              variant="ghost"
              onClick={() => setModal(null)}
              disabled={createContactMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={saveContact}
              loading={createContactMutation.isPending}
              disabled={!contactForm.name.trim()}
            >
              Save contact
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <Field label="Name" htmlFor="contact-name" error={formError && !contactForm.name.trim() ? formError : undefined}>
            <Input
              id="contact-name"
              value={contactForm.name}
              onChange={(event) =>
                setContactForm((prev) => ({ ...prev, name: event.target.value }))
              }
              placeholder="Full name"
              autoFocus
            />
          </Field>
          <Field label="Phone" htmlFor="contact-phone">
            <Input
              id="contact-phone"
              value={contactForm.phone}
              onChange={(event) =>
                setContactForm((prev) => ({ ...prev, phone: event.target.value }))
              }
              placeholder="+977…"
            />
          </Field>
          <Field label="Email" htmlFor="contact-email">
            <Input
              id="contact-email"
              type="email"
              value={contactForm.email}
              onChange={(event) =>
                setContactForm((prev) => ({ ...prev, email: event.target.value }))
              }
              placeholder="name@company.com"
            />
          </Field>
          <Field
            label="Group"
            htmlFor="contact-group"
            hint={
              contactGroups.length === 0
                ? "No groups yet — create one from the Groups tab, or leave ungrouped."
                : "Choose from available contact groups."
            }
          >
            <Select
              id="contact-group"
              value={contactForm.groupId}
              onChange={(event) =>
                setContactForm((prev) => ({ ...prev, groupId: event.target.value }))
              }
              options={groupOptions}
            />
          </Field>
          <Field label="Description" htmlFor="contact-description">
            <Textarea
              id="contact-description"
              rows={3}
              value={contactForm.description}
              onChange={(event) =>
                setContactForm((prev) => ({
                  ...prev,
                  description: event.target.value,
                }))
              }
              placeholder="Notes about this contact"
            />
          </Field>
          {formError && contactForm.name.trim() ? (
            <p className="text-xs text-danger">{formError}</p>
          ) : null}
          <p className="text-xs text-fg-subtle">
            This contact will be marked as added by{" "}
            <span className="font-medium text-fg">
              {profile?.name || profile?.username || "you"}
            </span>
            .
          </p>
        </div>
      </Modal>

      <Modal
        open={modal === "group"}
        onClose={() => {
          if (createGroupMutation.isPending) return;
          setModal(null);
          setFormError(null);
        }}
        title="Add group"
        footer={
          <>
            <Button
              variant="ghost"
              onClick={() => setModal(null)}
              disabled={createGroupMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={saveGroup}
              loading={createGroupMutation.isPending}
              disabled={!groupForm.name.trim()}
            >
              Save group
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <Field label="Name" htmlFor="group-name">
            <Input
              id="group-name"
              value={groupForm.name}
              onChange={(event) =>
                setGroupForm((prev) => ({ ...prev, name: event.target.value }))
              }
              placeholder="Sales, Support…"
              autoFocus
            />
          </Field>
          <Field label="Description" htmlFor="group-description">
            <Textarea
              id="group-description"
              rows={3}
              value={groupForm.description}
              onChange={(event) =>
                setGroupForm((prev) => ({
                  ...prev,
                  description: event.target.value,
                }))
              }
              placeholder="Optional description"
            />
          </Field>
          {formError ? <p className="text-xs text-danger">{formError}</p> : null}
        </div>
      </Modal>
    </div>
  );
}

function PillTab({
  active,
  label,
  count,
  onClick,
}: {
  active: boolean;
  label: string;
  count: number;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "inline-flex h-7 items-center gap-1.5 rounded-full px-2.5 text-xs font-semibold transition-colors",
        active
          ? "bg-papaya-deep text-white"
          : "bg-surface-muted text-fg-muted hover:bg-surface-muted/80 hover:text-fg",
      )}
    >
      {label}
      <span
        className={cn(
          "inline-flex min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-bold",
          active ? "bg-white/20 text-white" : "bg-surface text-fg-subtle",
        )}
      >
        {count}
      </span>
    </button>
  );
}

function ContactRow({
  contact,
  active,
  onSelect,
}: {
  contact: ContactRecord;
  active: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        "flex w-full items-center gap-3 rounded-[6px] px-2 py-2.5 text-left transition-colors",
        active ? "bg-surface-muted" : "hover:bg-surface-muted/70",
      )}
    >
      <span className="relative inline-flex h-11 w-11 items-center justify-center rounded-full bg-papaya/15 text-sm font-semibold text-papaya-deep">
        {contact.initials}
        {contact.online ? (
          <span className="absolute right-0 bottom-0 h-2.5 w-2.5 rounded-full border-2 border-surface bg-papaya-leaf" />
        ) : null}
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex items-center justify-between gap-2">
          <span className="truncate text-sm font-semibold text-fg">
            {contact.name}
            {contact.isMe ? (
              <span className="ml-1 font-medium text-fg-muted">(me)</span>
            ) : null}
          </span>
          {contact.favourite ? (
            <Star className="h-3.5 w-3.5 shrink-0 fill-papaya text-papaya" />
          ) : null}
        </span>
        <span className="mt-0.5 block truncate text-xs text-fg-muted">
          {contact.source === "internal"
            ? contact.extension || contact.phone || "No number"
            : `${contact.department}${contact.phone ? ` · ${contact.phone}` : ""}`}
        </span>
      </span>
    </button>
  );
}

function ContactInfoPanel({
  contact,
  groups,
  currentUserId,
  onClose,
  onDial,
  onMessage,
  messaging,
}: {
  contact: ContactRecord;
  groups: ContactGroup[];
  currentUserId?: number;
  onClose: () => void;
  onDial: () => void;
  onMessage: () => void;
  messaging?: boolean;
}) {
  const toggleFavourite = useFavouritesStore((s) => s.toggleFavourite);
  const starred = useFavouritesStore((s) => {
    if (!contact.phone) return false;
    const key = favouriteKeyFromPhone(contact.phone);
    return s.items.some((item) => item.key === key);
  });
  const linkedGroups = groups.filter((group) => contact.groupIds.includes(group.id));
  const addedByYou =
    currentUserId != null &&
    contact.createdById != null &&
    Number(contact.createdById) === Number(currentUserId);
  const addedByLabel = addedByYou
    ? "You"
    : contact.createdBy || "Unknown";
  const addedAt = formatDate(contact.createdAt);

  return (
    <>
      <header className="flex items-center justify-between border-b border-border bg-surface px-4 py-3">
        <h3 className="text-base font-semibold text-fg">Contact info</h3>
        <IconBtn label="Close" onClick={onClose}>
          <X className="h-4 w-4" />
        </IconBtn>
      </header>

      <div className="flex-1 overflow-y-auto p-4">
        <div className="rounded-[6px] border border-border bg-surface">
          <div className="flex items-center gap-3 border-b border-border px-4 py-4">
            <span className="relative inline-flex h-14 w-14 items-center justify-center rounded-full bg-papaya/15 text-lg font-semibold text-papaya-deep">
              {contact.initials}
            </span>
            <div className="min-w-0 flex-1">
              <p className="truncate text-base font-semibold text-fg">
                {contact.name}
                {contact.isMe ? (
                  <span className="ml-1.5 text-sm font-medium text-fg-muted">(me)</span>
                ) : null}
              </p>
              <p className="truncate text-sm text-fg-muted">{contact.role}</p>
              <p className="mt-0.5 text-xs text-fg-subtle">
                {contact.source === "internal" ? "Internal" : contact.department}
              </p>
            </div>
            <div className="flex items-center gap-1 text-fg-muted">
              {contact.phone?.trim() ? (
                <IconBtn
                  label={starred ? "Remove from favourites" : "Add to favourites"}
                  onClick={() =>
                    toggleFavourite({
                      name: contact.name,
                      number: contact.phone,
                      contactId: contact.id,
                    })
                  }
                >
                  <Star
                    className={cn(
                      "h-4 w-4",
                      starred && "fill-papaya text-papaya",
                    )}
                  />
                </IconBtn>
              ) : null}
              <IconBtn
                label={
                  messaging
                    ? "Opening chat…"
                    : contact.isMe
                      ? "Open notes"
                      : "Message"
                }
                onClick={onMessage}
                disabled={messaging}
              >
                <MessageSquare className="h-4 w-4" />
              </IconBtn>
              {!contact.isMe ? (
                <IconBtn label="Call" onClick={onDial} disabled={!contact.phone?.trim()}>
                  <Phone className="h-4 w-4" />
                </IconBtn>
              ) : null}
            </div>
          </div>

          <div className="space-y-4 px-4 py-4">
            {contact.source === "internal" && contact.extension ? (
              <InfoBlock
                label="Extension"
                value={contact.extension}
                icon={<Phone className="h-4 w-4" />}
              />
            ) : contact.phone ? (
              <InfoBlock
                label="Phone"
                value={contact.phone}
                icon={<Phone className="h-4 w-4" />}
              />
            ) : null}
            {contact.email ? (
              <InfoBlock label="Email" value={contact.email} icon={<Mail className="h-4 w-4" />} />
            ) : null}
            {contact.description ? (
              <InfoBlock
                label="Description"
                value={contact.description}
                icon={<UserPlus className="h-4 w-4" />}
              />
            ) : null}
            {contact.createdBy || contact.createdById != null || addedAt ? (
              <InfoBlock
                label="Added by"
                value={addedAt ? `${addedByLabel} · ${addedAt}` : addedByLabel}
                icon={<Users className="h-4 w-4" />}
              />
            ) : null}
            {linkedGroups.length > 0 ? (
              <div>
                <p className="mb-2 text-xs font-semibold tracking-wide text-fg-subtle uppercase">
                  Groups
                </p>
                <div className="flex flex-wrap gap-2">
                  {linkedGroups.map((group) => (
                    <span
                      key={group.id}
                      className="rounded-full bg-surface-muted px-2.5 py-1 text-xs font-medium text-fg-muted"
                    >
                      {group.name}
                    </span>
                  ))}
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </>
  );
}

function InfoBlock({
  label,
  value,
  icon,
}: {
  label: string;
  value: string;
  icon: ReactNode;
}) {
  return (
    <div className="flex items-start gap-3">
      <span className="mt-0.5 inline-flex h-8 w-8 items-center justify-center rounded-full bg-papaya-leaf/10 text-papaya-leaf">
        {icon}
      </span>
      <div className="min-w-0">
        <p className="text-xs font-semibold tracking-wide text-fg-subtle uppercase">{label}</p>
        <p className="mt-0.5 text-sm font-medium text-fg">{value}</p>
      </div>
    </div>
  );
}

function IconBtn({
  label,
  children,
  onClick,
  disabled,
}: {
  label: string;
  children: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={onClick}
      disabled={disabled}
      className="inline-flex h-9 w-9 items-center justify-center rounded-full text-fg-muted hover:bg-surface-muted hover:text-fg disabled:cursor-not-allowed disabled:opacity-40"
    >
      {children}
    </button>
  );
}
